/**
 * =============================================================================
 * TOOLS BAR - LEFT SIDEBAR ACTION ITEMS (Email, SMS, Wait)
 * =============================================================================
 * 
 * PURPOSE:
 * This component renders the LEFT SIDEBAR containing action items that can be
 * dragged to the workflow canvas to create new steps.
 * 
 * DRAG-AND-DROP:
 * Uses @dnd-kit's useDraggable hook for each tool item.
 * Drag data includes DRAG_TYPES.TOOL and the stepType to create.
 * 
 * LAYOUT:
 * - Desktop: Vertical sidebar (w-16 = 64px wide)
 * - Mobile: Horizontal scrollable bar at top of screen
 * =============================================================================
 */
import React from 'react';
import { Play, Mail, MessageSquare, Clock } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { DRAG_TYPES } from '../constants/dragTypes';

/**
 * DraggableTool - A single draggable tool item
 * 
 * @param {Object} props
 * @param {string} props.id - Unique ID for the draggable (e.g., 'tool-email')
 * @param {string} props.stepType - Type of step to create ('email' | 'sms' | 'wait')
 * @param {Component} props.icon - Lucide icon component
 * @param {string} props.label - Display label
 * @param {string} props.iconColor - Tailwind color class for icon
 * @param {boolean} props.isMobile - Mobile layout flag
 */
const DraggableTool = ({ id, stepType, icon: Icon, label, iconColor = 'text-stone-500', isMobile, isReadOnly = false }) => {
  // =============================================================================
  // @dnd-kit DRAGGABLE HOOK
  // =============================================================================
  // useDraggable makes this tool item a drag source.
  // - setNodeRef: Ref to attach to the draggable DOM element
  // - attributes: ARIA attributes for accessibility
  // - listeners: Event handlers (onPointerDown, etc.) for drag
  // - isDragging: Whether this item is currently being dragged
  // - data: Custom payload that handleDragEnd will receive
  // =============================================================================
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id,
    disabled: isReadOnly,
    data: {
      type: DRAG_TYPES.TOOL,
      stepType, // 'email' | 'sms' | 'wait'
    },
  });

  // Visual feedback when dragging or read-only
  const style = {
    opacity: isDragging ? 0.5 : isReadOnly ? 0.4 : 1,
  };

  if (isMobile) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex flex-col items-center gap-1 flex-shrink-0 touch-none ${isReadOnly ? 'cursor-not-allowed' : 'cursor-grab'}`}
        {...(!isReadOnly ? listeners : {})}
        {...(!isReadOnly ? attributes : {})}
      >
        <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-notion-border shadow-sm ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[9px] font-medium text-stone-500">{label}</span>
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col items-center gap-1 touch-none ${isReadOnly ? 'cursor-not-allowed' : 'cursor-grab'}`}
      {...(!isReadOnly ? listeners : {})}
      {...(!isReadOnly ? attributes : {})}
    >
      <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-notion-border shadow-sm ${iconColor} hover:border-stone-300 hover:shadow-md transition`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[9px] font-medium text-stone-500">{label}</span>
    </div>
  );
};

/**
 * ToolsBar Component - Left sidebar with draggable action items
 * 
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether to render mobile or desktop layout
 */
const ToolsBar = ({ isMobile, isReadOnly = false }) => {
  // ========== MOBILE LAYOUT ==========
  if (isMobile) {
    return (
      <div className="bg-white border-b border-notion-border flex items-center px-4 py-3 gap-4 overflow-x-auto z-20 flex-shrink-0">
        <span className="text-[9px] font-bold text-stone-400 uppercase tracking-wider mr-1">Tools</span>

        {/* Trigger - Disabled, cannot be added */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0 opacity-50">
          <div className="w-10 h-10 bg-stone-50 rounded-lg flex items-center justify-center border border-stone-200 text-stone-400">
            <Play className="w-5 h-5" />
          </div>
          <span className="text-[9px] font-medium text-stone-400">Trigger</span>
        </div>

        <div className="w-px h-6 bg-stone-200 flex-shrink-0"></div>

        {/* Draggable tools */}
        <DraggableTool
          id="tool-email"
          stepType="email"
          icon={Mail}
          label="Email"
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
        <DraggableTool
          id="tool-sms"
          stepType="sms"
          icon={MessageSquare}
          label="SMS"
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
        <DraggableTool
          id="tool-wait"
          stepType="wait"
          icon={Clock}
          label="Wait"
          iconColor="text-orange-400"
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
      </div>
    );
  }

  // ========== DESKTOP LAYOUT ==========
  return (
    <div className="w-16 bg-white border-r border-notion-border flex flex-col items-center py-6 gap-6 z-20 shadow-sm">
      {/* Trigger - Disabled */}
      <div className="flex flex-col items-center gap-1 opacity-50 cursor-not-allowed">
        <div className="w-10 h-10 bg-stone-50 rounded-lg flex items-center justify-center border border-stone-200 shadow-sm text-stone-400">
          <Play className="w-5 h-5" />
        </div>
        <span className="text-[9px] font-medium text-stone-400">Trigger</span>
      </div>

      <div className="h-px w-8 bg-stone-200"></div>

      {/* Draggable tools */}
      <DraggableTool
        id="tool-email"
        stepType="email"
        icon={Mail}
        label="Email"
        isMobile={isMobile}
        isReadOnly={isReadOnly}
      />
      <DraggableTool
        id="tool-sms"
        stepType="sms"
        icon={MessageSquare}
        label="SMS"
        isMobile={isMobile}
        isReadOnly={isReadOnly}
      />
      <DraggableTool
        id="tool-wait"
        stepType="wait"
        icon={Clock}
        label="Wait"
        iconColor="text-orange-400"
        isMobile={isMobile}
        isReadOnly={isReadOnly}
      />
    </div>
  );
};

export default ToolsBar;
